import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, HostListener, OnDestroy } from '@angular/core';
import { TaskService } from '../task.service';
import { UserService, AuthenticationService } from '@app/_services';
import { User } from '@app/_models';
import { Subscription, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import * as moment from 'moment';

export interface FilterOption {
  field: string;
  label: string;
  type: 'select' | 'multiselect' | 'text' | 'date' | 'daterange' | 'user';
  options?: any[];
  value?: any;
}

export interface GroupOption {
  value: string;
  label: string;
}

export interface ColumnOption {
  field: string;
  header: string;
  visible: boolean;
}

@Component({
    standalone: false,
    selector: 'app-task-view-controls',
    templateUrl: './task-view-controls.component.html',
    styleUrls: ['./task-view-controls.component.css']
})
export class TaskViewControlsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() currentGroupBy: string = 'none';
  @Input() currentFilters: any = {};
  @Input() currentColumns: ColumnOption[] = [];
  @Output() groupByChange = new EventEmitter<string>();
  @Output() filtersChange = new EventEmitter<any>();
  @Output() columnsChange = new EventEmitter<ColumnOption[]>();

  showGroupMenu = false;
  showFilterMenu = false;
  showColumnsMenu = false;

  // Debounce subject for filter changes
  private filterChangeSubject = new Subject<void>();
  private filterChangeSubscription: Subscription;

  // Group options
  groupOptions: GroupOption[] = [
    { value: 'none', label: 'None' },
    { value: 'status', label: 'Status' },
    { value: 'assignee', label: 'Assignee' },
    { value: 'priority', label: 'Priority' },
    { value: 'dueDate', label: 'Due Date' },
    { value: 'createdDate', label: 'Created Date' },
    { value: 'completedDate', label: 'Completed Date' },
    { value: 'category', label: 'Category/Tags' }
  ];

  // Filter options
  filterOptions: FilterOption[] = [];
  activeFilters: FilterOption[] = [];

  // Status and Priority options
  statuses: any[] = [];
  priorities: any[] = [];
  users: User[] = [];

  currentUser: User;
  currentUserSubscription: Subscription;

  constructor(
    private taskService: TaskService,
    private userService: UserService,
    private authenticationService: AuthenticationService
  ) {
    this.statuses = this.taskService.statuses || [];
    this.priorities = this.taskService.priorities || [];
    
    // Get current user
    this.currentUser = this.authenticationService.currentUserValue;
    
    // Subscribe to current user changes
    this.currentUserSubscription = this.authenticationService.currentUser.subscribe(user => {
      this.currentUser = user;
    });

    // Set up debounced filter change subscription
    this.filterChangeSubscription = this.filterChangeSubject.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.emitFilterChanges();
    });
  }

  ngOnInit() {
    console.log('TaskViewControlsComponent ngOnInit - currentColumns:', this.currentColumns);
    console.log('TaskViewControlsComponent ngOnInit - currentGroupBy:', this.currentGroupBy);
    
    // Initialize filter options first
    this.initializeFilterOptions();
    
    // Initialize columns if not provided
    if (!this.currentColumns || this.currentColumns.length === 0) {
      console.log('Columns not provided, will wait for parent to set them');
      this.currentColumns = [];
    } else {
      console.log('Columns provided:', this.currentColumns.length, 'columns');
    }
    
    // Load users first, then sync filters
    this.loadUsers();

    this.syncFiltersFromParent();
    this.updateActiveFilters();
  }
  
  ngOnChanges(changes: SimpleChanges) {
    if (changes['currentColumns']) {
      console.log('Columns changed in ngOnChanges:', this.currentColumns?.length, 'columns');
      // Columns are being set from parent - ensure we have the latest
      // The columns menu will use this.currentColumns directly
    }
    if (changes['currentGroupBy']) {
      console.log('GroupBy changed in ngOnChanges:', this.currentGroupBy);
      // GroupBy is being updated
    }
    if (changes['currentFilters']) {
      // Sync filterOptions with currentFilters from parent
      this.syncFiltersFromParent();
    }
  }

  /**
   * Sync filterOptions with currentFilters from parent component
   * This ensures the dropdowns show the correct selected values
   */
  private syncFiltersFromParent(): void {
    if (!this.currentFilters || !this.filterOptions || this.filterOptions.length === 0) {
      return;
    }

    // Update each filter option with the value from currentFilters
    this.filterOptions.forEach(filterOption => {
      if (this.currentFilters.hasOwnProperty(filterOption.field)) {
        const filterValue = this.currentFilters[filterOption.field];
        
        // For user type filters, ensure we have the exact user object from users array
        if (filterOption.type === 'user' && filterValue) {
          if (this.users && this.users.length > 0) {
            // Get userId from filterValue (could be object or just userId)
            const userId = typeof filterValue === 'object' && filterValue.userId 
              ? filterValue.userId 
              : filterValue;
            
            // Find the exact user object from users array to ensure reference match
            const user = this.users.find(u => u.userId === userId);
            if (user) {
              // Ensure the user has fullName property (users array already has fullName from loadUsers)
              const userWithFullName = user as any;
              if (!userWithFullName.fullName) {
                userWithFullName.fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || '';
              }
              // Only update if different to avoid unnecessary change detection
              if (!filterOption.value || filterOption.value.userId !== user.userId) {
                filterOption.value = user;
              }
            } else if (filterValue && typeof filterValue === 'object' && filterValue.userId) {
              // If user not found in array but we have a user object, ensure it has fullName
              const filterValueWithFullName = filterValue as any;
              if (!filterValueWithFullName.fullName) {
                filterValueWithFullName.fullName = `${filterValue.firstName || ''} ${filterValue.lastName || ''}`.trim() || filterValue.username || '';
              }
              filterOption.value = filterValue;
            }
          } else if (filterValue && typeof filterValue === 'object' && filterValue.userId) {
            // Users not loaded yet, but we have a user object - ensure it has fullName
            const filterValueWithFullName = filterValue as any;
            if (!filterValueWithFullName.fullName) {
              filterValueWithFullName.fullName = `${filterValue.firstName || ''} ${filterValue.lastName || ''}`.trim() || filterValue.username || '';
            }
            filterOption.value = filterValue;
          }
        } else {
          // For non-user filters, update directly
          if (filterOption.value !== filterValue) {
            filterOption.value = filterValue;
          }
        }
      } else {
        // Clear filter if it's not in currentFilters
        if (filterOption.value !== null && filterOption.value !== '' && 
            (!Array.isArray(filterOption.value) || filterOption.value.length > 0)) {
          filterOption.value = filterOption.type === 'text' ? '' : 
                              filterOption.type === 'multiselect' ? [] : null;
        }
      }
    });

    // Update active filters display
    this.updateActiveFilters();
  }

  initializeFilterOptions() {
    this.filterOptions = [
      {
        field: 'name',
        label: 'Task Name',
        type: 'text',
        value: ''
      },
      {
        field: 'status',
        label: 'Status',
        type: 'multiselect',
        options: this.statuses,
        value: [1, 2] // Default: In Progress (1) and Not Started (2)
      },
      {
        field: 'priority',
        label: 'Priority',
        type: 'multiselect',
        options: this.priorities,
        value: []
      },
      {
        field: 'assignedTo',
        label: 'Assigned To',
        type: 'user',
        value: null
      },
      {
        field: 'createdBy',
        label: 'Created By',
        type: 'user',
        value: null
      },
      {
        field: 'requestedBy',
        label: 'Requested By',
        type: 'user',
        value: null
      },
      {
        field: 'dateDue',
        label: 'Due Date',
        type: 'daterange',
        value: null
      },
      {
        field: 'dateCreated',
        label: 'Created Date',
        type: 'daterange',
        value: null
      },
      {
        field: 'dateCompleted',
        label: 'Completed Date',
        type: 'daterange',
        value: null
      }
    ];
  }

  loadUsers() {
    this.userService.findAll().subscribe(response => {
      if (response && response.data && response.data.users) {
        this.users = response.data.users.map((user: User) => ({
          ...user,
          fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || ''
        }));
        // After users are loaded, sync filters to ensure user objects are matched
        this.syncFiltersFromParent();
      }
    });
  }

  updateActiveFilters() {
    this.activeFilters = this.filterOptions.filter(opt => {
      if (opt.type === 'text') {
        return opt.value && opt.value.trim() !== '';
      } else if (opt.type === 'multiselect') {
        return opt.value && opt.value.length > 0;
      } else if (opt.type === 'user') {
        return opt.value !== null;
      } else if (opt.type === 'daterange') {
        return opt.value !== null && opt.value.length > 0;
      }
      return false;
    });
  }

  onGroupSelect(groupBy: string) {
    console.log('onGroupSelect called with:', groupBy);
    this.currentGroupBy = groupBy;
    this.groupByChange.emit(groupBy);
    this.showGroupMenu = false;
    console.log('GroupBy changed to:', groupBy);
  }

  onFilterChange(filter: FilterOption) {
    // Update active filters immediately for UI feedback
    this.updateActiveFilters();
    
    // Trigger debounced filter change emission
    this.filterChangeSubject.next();
  }

  /**
   * Emit filter changes after debounce delay
   * This method is called by the debounced subject
   */
  private emitFilterChanges(): void {
    const filters: any = {};
    this.filterOptions.forEach(opt => {
      if (opt.value !== null && opt.value !== '' && 
          (!Array.isArray(opt.value) || opt.value.length > 0)) {
        filters[opt.field] = opt.value;
      }
    });
    this.filtersChange.emit(filters);
  }

  onColumnToggle(column: ColumnOption) {
    console.log('onColumnToggle called for column:', column.field, 'current visible:', column.visible);
    column.visible = !column.visible;
    console.log('Column visibility changed to:', column.visible);
    this.columnsChange.emit([...this.currentColumns]);
    console.log('Emitted columns change:', this.currentColumns.map(c => ({ field: c.field, visible: c.visible })));
  }

  removeFilter(filter: FilterOption) {
    filter.value = filter.type === 'text' ? '' : 
                   filter.type === 'multiselect' ? [] : 
                   filter.type === 'daterange' ? null : null;
    this.onFilterChange(filter);
  }

  /**
   * Handle user selection from user-search autocomplete component
   */
  onUserSelected(user: any, filter: FilterOption) {
    if (user) {
      filter.value = user;
      this.onFilterChange(filter);
    }
  }

  /**
   * Handle user cleared event from user-search autocomplete component
   */
  onUserCleared(user: any, filter: FilterOption) {
    filter.value = null;
    this.onFilterChange(filter);
  }

  clearAllFilters() {
    this.filterOptions.forEach(opt => {
      opt.value = opt.type === 'text' ? '' : 
                  opt.type === 'multiselect' ? [] : null;
    });
    this.updateActiveFilters();
    // Emit immediately when clearing all filters (no debounce needed)
    this.filtersChange.emit({});
    // Close the filter overlay after clearing
    this.showFilterMenu = false;
  }

  ngOnDestroy() {
    // Clean up subscriptions
    if (this.currentUserSubscription) {
      this.currentUserSubscription.unsubscribe();
    }
    if (this.filterChangeSubscription) {
      this.filterChangeSubscription.unsubscribe();
    }
    // Complete the subject
    this.filterChangeSubject.complete();
  }

  toggleGroupMenu() {
    console.log('toggleGroupMenu called, current state:', this.showGroupMenu);
    this.showGroupMenu = !this.showGroupMenu;
    this.showFilterMenu = false;
    this.showColumnsMenu = false;
    console.log('toggleGroupMenu new state:', this.showGroupMenu);
  }

  toggleFilterMenu() {
    console.log('toggleFilterMenu called');
    this.showFilterMenu = !this.showFilterMenu;
    this.showGroupMenu = false;
    this.showColumnsMenu = false;
  }

  toggleColumnsMenu() {
    console.log('toggleColumnsMenu called, currentColumns:', this.currentColumns);
    this.showColumnsMenu = !this.showColumnsMenu;
    this.showGroupMenu = false;
    this.showFilterMenu = false;
    console.log('toggleColumnsMenu new state:', this.showColumnsMenu);
  }

  getGroupLabel(): string {
    const option = this.groupOptions.find(opt => opt.value === this.currentGroupBy);
    return option ? option.label : 'None';
  }

  getFilterCount(): number {
    return this.activeFilters.length;
  }

  getUserLabel(user: User): string {
    if (!user) return '';
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || '';
  }

  getMultiselectDisplay(filter: FilterOption): string {
    if (!filter.value || !Array.isArray(filter.value) || filter.value.length === 0) {
      return '';
    }

    // Get option names from the options array
    const selectedNames: string[] = [];
    filter.value.forEach((selectedValue: any) => {
      const option = filter.options?.find((opt: any) => opt.value === selectedValue);
      if (option) {
        selectedNames.push(option.name || option.label || String(selectedValue));
      } else {
        selectedNames.push(String(selectedValue));
      }
    });

    // If more than 2 items, show first 2 and count
    if (selectedNames.length > 2) {
      return `${selectedNames.slice(0, 2).join(', ')} +${selectedNames.length - 2} more`;
    }

    return selectedNames.join(', ');
  }

  getDateRangeDisplay(filter: FilterOption): string {
    if (!filter.value || !Array.isArray(filter.value) || filter.value.length === 0) {
      return 'Date range';
    }

    if (filter.value.length === 1) {
      return moment(filter.value[0]).format('MMM D, YYYY');
    }

    if (filter.value.length === 2) {
      const start = moment(filter.value[0]).format('MMM D, YYYY');
      const end = moment(filter.value[1]).format('MMM D, YYYY');
      return `${start} - ${end}`;
    }

    return 'Date range';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // Close menus when clicking outside
    if (!(event.target as HTMLElement).closest('.control-dropdown')) {
      this.showGroupMenu = false;
      this.showFilterMenu = false;
      this.showColumnsMenu = false;
    }
  }
}

